import type { GridStackWidget } from 'gridstack';
import type { SensorData, SensorRun, WidgetOptions } from './dashboardTypes';
import {
  BOOLEAN_WIDGET_TYPE,
  MECHANISM_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  SENSOR_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
} from '../Graphs/sensor-utils';
import {
  collectElectronicSensorStreamKeysFromLayout,
  collectFloatStreamGraphKeysFromLayout,
  collectMotorStreamKeysFromLayout,
  collectOtherElectronicsStreamKeysFromLayout,
  collectUnionSensorKeysFromLayout,
  historicLayoutWidgetPorts,
  mergeStreamKeys,
  parseDashboardWidgetContent,
  portDisplayLabelsRecordFromWidgetOptions,
} from '../../utils/sensor-stream-tracker';

export type DashboardStreamKeyGroups = {
  motorStreamKeys: string[];
  electronicSensorStreamKeys: string[];
  otherElectronicsStreamKeys: string[];
  floatStreamGraphKeys: string[];
  layoutStreamKeys: string[];
};

export function portDisplayLabelMapFromWidgetOptions(options?: WidgetOptions): Map<number, string> {
  const merged = new Map<number, string>();
  if (!options) return merged;

  const labelsByPort = portDisplayLabelsRecordFromWidgetOptions(options);
  if (labelsByPort) {
    for (const [portKey, label] of Object.entries(labelsByPort)) {
      const port = Number(portKey);
      if (Number.isFinite(port) && label.trim()) merged.set(port, label);
    }
  }

  if (options.motorPortDisplayLabel?.trim()) {
    const ports = options.selectedMotorPorts?.length
      ? options.selectedMotorPorts
      : options.selectedSensorPorts?.length
      ? options.selectedSensorPorts
      : options.selectedMotorPort != null
      ? [options.selectedMotorPort]
      : [];
    for (const port of ports) {
      if (!merged.has(port)) merged.set(port, options.motorPortDisplayLabel);
    }
  }

  return merged;
}

export function buildPortDisplayLabelMapFromHistoricLayout(layout: GridStackWidget[]): Map<number, string> {
  const merged = new Map<number, string>();
  for (const widget of layout) {
    const content = parseDashboardWidgetContent(widget.content);
    for (const [port, label] of portDisplayLabelMapFromWidgetOptions(content?.options)) {
      if (!merged.has(port)) merged.set(port, label);
    }
  }
  return merged;
}

/** Variable name from a saved `Port N: variableName` (or lead-motor) display label. */
function variableNameFromSavedPortDisplayLabel(displayLabel: string): string {
  const match = /^Port\s+\d+:\s*(.+)$/i.exec(displayLabel.trim());
  if (!match) return '';
  let variableName = match[1].trim();
  const leadMotorSuffix = ' - Lead Motor';
  if (variableName.endsWith(leadMotorSuffix)) {
    variableName = variableName.slice(0, -leadMotorSuffix.length).trim();
  }
  return variableName;
}

type HistoricWidgetVariableNameKind = 'motor' | 'sensor' | 'other';

function variableNameForPortFromWidgetOptions(
  options: WidgetOptions | undefined,
  port: number,
  widgetPorts: readonly number[],
  kind: HistoricWidgetVariableNameKind
): string {
  if (!options) return '';

  const labelsByPort = portDisplayLabelsRecordFromWidgetOptions(options);
  const savedLabel = labelsByPort?.[String(port)]?.trim();
  if (savedLabel) {
    return variableNameFromSavedPortDisplayLabel(savedLabel);
  }

  if (kind === 'motor') {
    if (options.motorPortDisplayLabel?.trim() && widgetPorts.includes(port)) {
      return variableNameFromSavedPortDisplayLabel(options.motorPortDisplayLabel);
    }
    if (options.motorVariableName?.trim() && widgetPorts.length === 1 && widgetPorts[0] === port) {
      return options.motorVariableName.trim();
    }
    return '';
  }

  if (kind === 'sensor') {
    if (options.sensorVariableName?.trim() && widgetPorts.length === 1 && widgetPorts[0] === port) {
      return options.sensorVariableName.trim();
    }
    return '';
  }

  if (options.otherVariableName?.trim() && widgetPorts.length === 1 && widgetPorts[0] === port) {
    return options.otherVariableName.trim();
  }
  // Legacy other widgets stored the program variable name on motorVariableName before otherVariableName existed.
  if (options.motorVariableName?.trim() && widgetPorts.length === 1 && widgetPorts[0] === port) {
    return options.motorVariableName.trim();
  }

  return '';
}

export function buildSensorVariableNameByPortFromHistoricLayout(layout: GridStackWidget[]): Map<number, string> {
  const merged = new Map<number, string>();
  for (const widget of layout) {
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) continue;
    const isSensorWidget =
      content.name === SENSOR_WIDGET_TYPE || (content.name === BOOLEAN_WIDGET_TYPE && content.sensorKeys.some(isElectronicSensorStreamSensorKey));
    if (!isSensorWidget) continue;

    const widgetPorts = historicLayoutWidgetPorts(content);
    for (const port of widgetPorts) {
      const variableName = variableNameForPortFromWidgetOptions(content.options, port, widgetPorts, 'sensor');
      if (variableName && !merged.has(port)) merged.set(port, variableName);
    }
  }
  return merged;
}

export function buildMechanismVariableNameByPortFromHistoricLayout(layout: GridStackWidget[]): Map<number, string> {
  const merged = new Map<number, string>();
  for (const widget of layout) {
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) continue;
    const isMechanismWidget = content.name === MECHANISM_WIDGET_TYPE || content.sensorKeys.some((key) => key.startsWith('mechanism.'));
    if (!isMechanismWidget) continue;

    const widgetPorts = historicLayoutWidgetPorts(content);
    for (const port of widgetPorts) {
      const variableName = variableNameForPortFromWidgetOptions(content.options, port, widgetPorts, 'motor');
      if (variableName && !merged.has(port)) merged.set(port, variableName);
    }
  }
  return merged;
}

export function buildOtherElectronicsVariableNameByPortFromHistoricLayout(layout: GridStackWidget[]): Map<number, string> {
  const merged = new Map<number, string>();
  for (const widget of layout) {
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) continue;
    const isOtherWidget = content.name === OTHER_WIDGET_TYPE || content.sensorKeys.some(isOtherElectronicsStreamSensorKey);
    if (!isOtherWidget) continue;

    const widgetPorts = historicLayoutWidgetPorts(content);
    for (const port of widgetPorts) {
      const variableName = variableNameForPortFromWidgetOptions(content.options, port, widgetPorts, 'other');
      if (variableName && !merged.has(port)) merged.set(port, variableName);
    }
  }
  return merged;
}

export function collectDashboardStreamKeyGroups(
  isLiveMode: boolean,
  liveLayout: GridStackWidget[],
  historicRun: SensorRun | undefined
): DashboardStreamKeyGroups {
  if (isLiveMode) {
    return {
      motorStreamKeys: collectMotorStreamKeysFromLayout(liveLayout),
      electronicSensorStreamKeys: collectElectronicSensorStreamKeysFromLayout(liveLayout),
      otherElectronicsStreamKeys: collectOtherElectronicsStreamKeysFromLayout(liveLayout),
      floatStreamGraphKeys: collectFloatStreamGraphKeysFromLayout(liveLayout),
      layoutStreamKeys: collectUnionSensorKeysFromLayout(liveLayout),
    };
  }

  const motorStreamKeys = collectMotorStreamKeysFromHistoricRun(historicRun);
  const electronicSensorStreamKeys = collectElectronicSensorStreamKeysFromHistoricRun(historicRun);
  const otherElectronicsStreamKeys = collectOtherElectronicsStreamKeysFromHistoricRun(historicRun);
  const floatStreamGraphKeys = collectFloatStreamGraphKeysFromHistoricRun(historicRun);
  const categoryStreamKeys = mergeStreamKeys(
    mergeStreamKeys(motorStreamKeys, electronicSensorStreamKeys),
    mergeStreamKeys(otherElectronicsStreamKeys, floatStreamGraphKeys)
  );
  const layoutStreamKeys = historicRun ? mergeStreamKeys(categoryStreamKeys, collectUnionSensorKeysFromLayout(historicRun.dashboardLayout)) : [];

  return {
    motorStreamKeys,
    electronicSensorStreamKeys,
    otherElectronicsStreamKeys,
    floatStreamGraphKeys,
    layoutStreamKeys,
  };
}

function streamKeysWithSamplesInRun(sensorData: SensorData[], matchesKey: (key: string) => boolean): string[] {
  const keys: string[] = [];
  for (const datum of sensorData) {
    const stream = datum.pythonStream;
    if (!stream) continue;
    for (const [key, value] of Object.entries(stream)) {
      if (key === 'time') continue;
      if (!matchesKey(key)) continue;
      if (typeof value === 'number' && Number.isFinite(value)) keys.push(key);
    }
  }
  return keys;
}

function collectStreamKeysFromHistoricRun(
  run: SensorRun | undefined,
  collectFromLayout: (layout: GridStackWidget[]) => string[],
  matchesKey: (key: string) => boolean
): string[] {
  if (!run) return [];
  return mergeStreamKeys(collectFromLayout(run.dashboardLayout), streamKeysWithSamplesInRun(run.sensorData, matchesKey));
}

/** Motor stream keys for a historic run: saved layout plus any motor samples recorded in that run. */
export function collectMotorStreamKeysFromHistoricRun(run: SensorRun | undefined): string[] {
  return collectStreamKeysFromHistoricRun(run, collectMotorStreamKeysFromLayout, isMotorStreamSensorKey);
}

export function collectElectronicSensorStreamKeysFromHistoricRun(run: SensorRun | undefined): string[] {
  return collectStreamKeysFromHistoricRun(run, collectElectronicSensorStreamKeysFromLayout, isElectronicSensorStreamSensorKey);
}

export function collectOtherElectronicsStreamKeysFromHistoricRun(run: SensorRun | undefined): string[] {
  return collectStreamKeysFromHistoricRun(run, collectOtherElectronicsStreamKeysFromLayout, isOtherElectronicsStreamSensorKey);
}

export function collectFloatStreamGraphKeysFromHistoricRun(run: SensorRun | undefined): string[] {
  if (!run) return [];
  const layoutKeys = collectFloatStreamGraphKeysFromLayout(run.dashboardLayout);
  const layoutKeySet = new Set(layoutKeys);
  return mergeStreamKeys(
    layoutKeys,
    streamKeysWithSamplesInRun(run.sensorData, (key) => layoutKeySet.has(key))
  );
}

export function deriveSessionRunIds(allData: SensorRun[] | undefined): number[] | null {
  if (!allData?.length) return null;
  return allData.map((datum) => datum.runId);
}

/** Resolves the historic session to display, falling back to the latest saved run. */
export function resolveHistoricRun(allData: SensorRun[], runDate: number | null): SensorRun | undefined {
  if (!allData.length) return undefined;
  const selected = allData.find((run) => run.runId === Number(runDate));
  return selected ?? allData[allData.length - 1];
}

/** Returns the dashboard layout captured with a historic session. */
export function resolveHistoricLayout(allData: SensorRun[], runDate: number | null): GridStackWidget[] {
  return resolveHistoricRun(allData, runDate)?.dashboardLayout ?? [];
}

export function detectProgramStopTransition(prevIsProgramRunning: boolean, isProgramRunning: boolean): boolean {
  return prevIsProgramRunning && !isProgramRunning;
}

/** Wait for Dexie/useLiveQuery to surface the just-finished run before hydrating historic charts. */
export function shouldDeferHistoricLoad(options: { skipNextHistoricalLoad: boolean; allData: SensorRun[] | undefined }): boolean {
  if (!options.skipNextHistoricalLoad) return false;
  return !options.allData?.length;
}

/** Avoid wiping charts while waiting for Dexie to propagate a just-finished run. */
export function shouldClearHistoricGraphsToPlaceholder(options: { allData: SensorRun[] | undefined; deferHistoricLoad: boolean }): boolean {
  if (options.deferHistoricLoad) return false;
  return !options.allData?.length;
}

export function shouldAutoSelectLatestRunDate(options: { allData: SensorRun[] | undefined; previousRunCount: number }): number | null {
  if (!options.allData?.length) return null;
  if (options.allData.length <= options.previousRunCount) return null;
  return options.allData[options.allData.length - 1]?.runId ?? null;
}
